import { type HTMLAttributes, type ReactNode } from 'react';
import { CloseIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export type ChipVariant = 'default' | 'walk_in' | 'blockout';

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  readonly selected?: boolean;
  readonly icon?: ReactNode;
  /** When set, renders a dismiss × button. The handler is called when clicked. */
  readonly onDismiss?: () => void;
  readonly dismissLabel?: string;
  /**
   * Domain variant. `'default'` is the existing neutral / selected visual
   * already shipped — every existing caller falls back to it. `'walk_in'`
   * tints the chip with the `info` palette (matches the F18 walk-in pin
   * styling on `CalendarSlotGrid variant='manage'`). `'blockout'` uses the
   * `warning` palette to mirror staff blockouts on the calendar.
   */
  readonly variant?: ChipVariant;
}

const VARIANT_STYLES: Record<Exclude<ChipVariant, 'default'>, string> = {
  walk_in: 'bg-info/15 text-ink border border-info/30 hover:bg-info/20',
  blockout: 'bg-warning/15 text-ink border border-warning/30 hover:bg-warning/20',
};

export function Chip({
  selected,
  icon,
  onDismiss,
  dismissLabel = 'Remove',
  variant = 'default',
  className,
  children,
  ...rest
}: ChipProps) {
  // `selected` always wins (preserves existing F02/F14 filter-chip UX). Domain
  // variants only paint when `selected` is falsy — they're status pills, not
  // selectable toggles.
  const isDefault = variant === 'default';
  const variantClasses = !selected && !isDefault ? VARIANT_STYLES[variant] : null;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[12px] font-semibold whitespace-nowrap',
        'transition-colors duration-[120ms]',
        selected
          ? 'bg-primary text-primary-ink'
          : isDefault
            ? 'bg-surface-2 text-ink-2 border border-hairline-2 hover:bg-surface'
            : variantClasses,
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span
          className={cn(
            'inline-flex shrink-0',
            !selected && variant === 'walk_in' && 'text-info',
            !selected && variant === 'blockout' && 'text-warning',
          )}
        >
          {icon}
        </span>
      ) : null}
      {children}
      {onDismiss ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          aria-label={dismissLabel}
          className={cn(
            'ms-1 -me-1 inline-flex items-center justify-center size-4 rounded-full',
            'hover:bg-black/10 dark:hover:bg-white/10',
            'focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40',
          )}
        >
          <CloseIcon size={12} color="currentColor" />
        </button>
      ) : null}
    </span>
  );
}
